import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Home, Search, Heart, MessageSquare, User, Bell, LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const NAV = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/kategoriler", label: "Kategoriler" },
  { to: "/firmalar", label: "Firmalar" },
  { to: "/#nasil-calisir", label: "Nasıl Çalışır?", hash: true },
  { to: "/kayit?tip=firma", label: "Firma Ol" },
];

function NotifBell() {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
  }, [user]);
  if (!user) return null;
  const target = user.role === "COMPANY" ? "/firma-panel?tab=bildirimler" : user.role === "CUSTOMER" ? "/panel?tab=bildirimler" : "/admin";
  return (
    <button data-testid="notifications-bell" onClick={() => navigate(target)}
      className="relative p-2 rounded-xl border border-white/10 hover:border-[#D4AF37]/50 text-slate-300 hover:text-[#D4AF37] transition-colors">
      <Bell size={18} />
      {unread > 0 && (
        <span data-testid="notifications-unread-count" className="absolute -top-1.5 -right-1.5 bg-[#EF4444] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
          {unread}
        </span>
      )}
    </button>
  );
}

export default function Layout({ children }) {
  const { user, logout, panelPath } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070C1E]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="text-2xl font-extrabold tracking-tight text-white shrink-0" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            etkinlik<span className="text-[#D4AF37]">.</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) =>
              n.hash ? (
                <a key={n.label} href={n.to} data-testid={`nav-${n.label}`} className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-[#D4AF37] transition-colors">{n.label}</a>
              ) : (
                <NavLink key={n.label} to={n.to} data-testid={`nav-${n.label}`}
                  className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-[#D4AF37]" : "text-slate-300 hover:text-[#D4AF37]"}`}>
                  {n.label}
                </NavLink>
              )
            )}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <NotifBell />
            {user ? (
              <>
                <button data-testid="header-panel-btn" onClick={() => navigate(panelPath())} className="btn-ghost !py-2">
                  <LayoutDashboard size={16} /> Panelim
                </button>
                <button data-testid="header-logout-btn" onClick={() => { logout(); navigate("/"); }} className="p-2 rounded-xl border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/40 transition-colors">
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <>
                <Link to="/giris" data-testid="header-login-btn" className="text-sm font-semibold text-slate-200 hover:text-[#D4AF37] transition-colors px-2">Giriş Yap</Link>
                <Link to="/kayit?tip=firma" data-testid="header-company-register-btn" className="btn-gold !py-2.5">Ücretsiz Firma Kaydı</Link>
              </>
            )}
          </div>
          <button className="lg:hidden p-2 text-slate-200" data-testid="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {mobileOpen && (
          <div className="lg:hidden border-t border-white/10 bg-[#070C1E]/95 backdrop-blur-xl px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link key={n.label} to={n.hash ? "/" : n.to} onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium text-slate-200 rounded-lg hover:bg-white/5">{n.label}</Link>
            ))}
            <div className="flex gap-2 mt-2">
              {user ? (
                <button onClick={() => { setMobileOpen(false); navigate(panelPath()); }} className="btn-gold flex-1 !py-2.5">Panelim</button>
              ) : (
                <>
                  <Link to="/giris" onClick={() => setMobileOpen(false)} className="btn-ghost flex-1 !py-2.5">Giriş Yap</Link>
                  <Link to="/kayit?tip=firma" onClick={() => setMobileOpen(false)} className="btn-gold flex-1 !py-2.5">Firma Kaydı</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pb-20 md:pb-0">{children}</main>

      <footer className="border-t border-white/10 bg-[#050914] mt-16 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 pt-10">
          <div className="card p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-5" data-testid="footer-contact">
            <div>
              <div className="font-extrabold text-white text-lg">Bizimle iletişime geçebilirsiniz</div>
              <p className="text-sm text-slate-400 mt-1">Soru, öneri veya reklam iş birliği için bize her zaman ulaşabilirsiniz.</p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <a href="mailto:info@etkinlik.demo" data-testid="footer-contact-email" className="btn-ghost !py-2.5">info@etkinlik.demo</a>
              <a href="tel:08500000000" data-testid="footer-contact-phone" className="btn-ghost !py-2.5">0850 000 00 00</a>
              <a href="https://wa.me/905000000000" target="_blank" rel="noreferrer" data-testid="footer-contact-whatsapp" className="btn-gold !py-2.5">WhatsApp Destek</a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="text-xl font-extrabold text-white mb-3">etkinlik<span className="text-[#D4AF37]">.</span></div>
            <p className="text-slate-400 leading-relaxed">Türkiye'nin etkinlik hizmetleri pazaryeri. Tarihinizi seçin, müsait hizmetleri keşfedin.</p>
          </div>
          <div>
            <div className="font-bold text-slate-200 mb-3">Platform</div>
            <div className="flex flex-col gap-2 text-slate-400">
              <Link to="/kategoriler" className="hover:text-[#D4AF37]">Kategoriler</Link>
              <Link to="/firmalar" className="hover:text-[#D4AF37]">Firmalar</Link>
              <Link to="/ara" className="hover:text-[#D4AF37]">Müsaitlik Ara</Link>
            </div>
          </div>
          <div>
            <div className="font-bold text-slate-200 mb-3">Firmalar İçin</div>
            <div className="flex flex-col gap-2 text-slate-400">
              <Link to="/kayit?tip=firma" className="hover:text-[#D4AF37]">Ücretsiz Firma Kaydı</Link>
              <Link to="/giris" className="hover:text-[#D4AF37]">Firma Girişi</Link>
            </div>
          </div>
          <div>
            <div className="font-bold text-slate-200 mb-3">İletişim</div>
            <div className="flex flex-col gap-2 text-slate-400">
              <a href="mailto:info@etkinlik.demo" className="hover:text-[#D4AF37]">info@etkinlik.demo</a>
              <a href="tel:08500000000" className="hover:text-[#D4AF37]">0850 000 00 00</a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/5 py-4 text-center text-xs text-slate-600">© 2026 etkinlik. — Demo</div>
      </footer>

      <nav data-testid="mobile-bottom-nav" className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0B132B]/95 backdrop-blur-xl border-t border-white/10">
        <div className="grid grid-cols-5">
          {[
            { to: "/", label: "Ana Sayfa", icon: Home, tid: "mobile-nav-home" },
            { to: "/ara", label: "Ara", icon: Search, tid: "mobile-nav-search" },
            { to: "/panel?tab=favoriler", label: "Favoriler", icon: Heart, tid: "mobile-nav-favorites" },
            { to: "/panel?tab=talepler", label: "Mesajlar", icon: MessageSquare, tid: "mobile-nav-messages" },
            { to: user ? panelPath() : "/giris", label: "Profil", icon: User, tid: "mobile-nav-profile" },
          ].map((i) => (
            <NavLink key={i.tid} to={i.to} data-testid={i.tid}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold transition-colors ${isActive ? "text-[#D4AF37]" : "text-slate-400"}`}>
              <i.icon size={20} />
              {i.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
